/**
 * 持久化门面：把"用哪个后端"与"业务仓库"分开，并负责**如实告知用户当前能不能保存**。
 *
 * ## 降级：绝不假装保存成功
 *
 * 隐私模式、禁用站点数据、受限上下文（例如没有 `indexedDB` 的 Worker）都会让 IndexedDB 打不开。
 * 此时的做法是：
 *
 * 1. 换成内存实现，应用照常能跑（用户至少能完成这次飞行）；
 * 2. `durable === false`，并且 `notices` 里有一条**明确**的中文说明
 *    "本次会话的改动不会被保存，刷新或关闭页面即丢失"；
 * 3. **不动库里的一个字节**。迁移失败、库里版本比程序新时同样如此 ——
 *    宁可这次不持久化，也不会为了"让它能打开"而删掉用户的参数快照与审计日志。
 *
 * 静默失败是这里最坏的选项：用户以为航线草稿存住了，回家才发现没了，
 * 比一开始就告诉他"这次不会保存"糟糕得多。
 *
 * ## 失败提示的汇聚
 *
 * 各仓库把失败归类成 `StorageFailure` 后都会推一条到 `notices`，`onNotice` 让界面立刻弹提示。
 * 于是"配额满了""存储被禁用了"这类消息只有一个出口，不会出现某个面板悄悄吞掉失败、
 * 另一个面板弹一个开发期错误码。
 */

import { AuditLog, type AuditRetention, type AuditRotationResult } from './audit';
import { describeStorageFailure, type SaveOutcome, type StorageFailure, type StorageFailureCode } from './errors';
import { IndexedDbStore } from './indexeddb';
import { PERSISTENCE_SCHEMA_VERSION } from './keys';
import { MemoryStore } from './memory';
import { MissionDraftRepository } from './mission-drafts';
import { ParamSnapshotRepository } from './param-snapshots';
import { PreflightRepository } from './preflight';
import type { MigrationIssue } from './schema';
import { SettingsRepository, type AppSettings } from './settings';
import type { KeyValueStore } from './store';

/** 默认数据库名。写死在代码里而不是做成配置项：改它等于所有用户的历史数据"消失" */
export const DEFAULT_DATABASE_NAME = 'hgc';

export interface PersistenceNotice {
  readonly level: 'warning' | 'error';
  readonly code: StorageFailureCode;
  /** 中文可读消息 */
  readonly message: string;
  /** 建议动作 */
  readonly advice: string;
}

export type PersistenceBackend = 'indexeddb' | 'memory';

export interface PersistenceOptions {
  readonly databaseName?: string;
  /**
   * 注入 `IDBFactory`。传 `null` 表示"这个环境不许用 IndexedDB"，用来走降级路径；
   * 不传则读 `globalThis.indexedDB`。
   */
  readonly indexedDB?: IDBFactory | null;
  readonly now?: () => number;
  /** 应用版本号，会写进数据库自述记录，用于排查"这份数据是谁写的" */
  readonly appVersion?: string;
  /** 审计日志写入者 id；默认随机（每个标签页一个） */
  readonly writerId?: string;
  readonly blockedTimeoutMs?: number;
  readonly onNotice?: (notice: PersistenceNotice) => void;
  readonly onFailure?: (failure: StorageFailure) => void;
  /** 设置读取失败时的默认值 */
  readonly settingsDefaults?: AppSettings;
  readonly auditOnRotate?: (result: AuditRotationResult) => void;
  /** 本地保留的航线草稿份数上限，默认 5 */
  readonly maxDrafts?: number;
  /** 保留的飞前检查记录份数上限，默认 200 */
  readonly maxPreflightRecords?: number;
  /** id 生成器（草稿 / 检查记录），测试注入确定性实现 */
  readonly newId?: () => string;
}

export interface PersistenceStatus {
  readonly backend: PersistenceBackend;
  readonly durable: boolean;
  readonly schemaVersion: number;
  /** 打开前库里的版本；0 表示这次是新建 */
  readonly previousSchemaVersion: number;
  /** 迁移时没能改写、但**原样保留**在库里的旧记录 */
  readonly migrationIssues: readonly MigrationIssue[];
  readonly databaseName: string;
}

export interface Persistence {
  readonly backend: PersistenceBackend;
  /**
   * `false` 表示"本次会话的改动不会被保存"。界面必须在显眼处显示这一点，
   * 而不是等用户关闭页面后才从丢失的草稿里发现。
   */
  readonly durable: boolean;
  readonly store: KeyValueStore;
  readonly settings: SettingsRepository;
  readonly params: ParamSnapshotRepository;
  readonly drafts: MissionDraftRepository;
  readonly preflight: PreflightRepository;
  readonly audit: AuditLog;
  /** 当前的提示（含打开时的降级说明与之后的保存失败） */
  readonly notices: readonly PersistenceNotice[];
  readonly status: PersistenceStatus;
  /** 清掉已显示的提示（界面关掉提示条后调用） */
  dismissNotices(): void;
  /** 调整审计容量：写进设置并立刻生效 */
  setAuditRetention(retention: Partial<AuditRetention>): Promise<SaveOutcome<AppSettings>>;
  close(): Promise<void>;
}

interface AssembleContext {
  readonly durable: boolean;
  readonly previousSchemaVersion: number;
  readonly migrationIssues: readonly MigrationIssue[];
  readonly databaseName: string;
  readonly now: () => number;
  readonly extraNotice?: PersistenceNotice;
}

/**
 * 打开持久化。**永不抛错**：最坏情况是降级到内存并给出说明 ——
 * 地面站因为"存不了设置"而打不开是本末倒置。
 */
export async function openPersistence(options: PersistenceOptions = {}): Promise<Persistence> {
  const databaseName = options.databaseName ?? DEFAULT_DATABASE_NAME;
  const factory = resolveFactory(options);

  if (factory === null) {
    return await assemble(new MemoryStore(), options, {
      durable: false,
      previousSchemaVersion: 0,
      migrationIssues: [],
      databaseName,
      now: options.now ?? Date.now,
      extraNotice: {
        level: 'warning',
        code: 'unavailable',
        message:
          '当前环境没有可用的 IndexedDB（可能处于隐私模式、站点数据被禁用，或页面运行在受限上下文中）。本次会话的改动不会被保存。',
        advice: '刷新或关闭页面后这些改动会丢失。请退出隐私模式，或允许本站保存站点数据。',
      },
    });
  }

  try {
    const store = await IndexedDbStore.open({
      name: databaseName,
      factory,
      ...(options.now === undefined ? {} : { now: options.now }),
      ...(options.appVersion === undefined ? {} : { writtenBy: `HGC ${options.appVersion}` }),
      ...(options.blockedTimeoutMs === undefined ? {} : { blockedTimeoutMs: options.blockedTimeoutMs }),
    });
    return await assemble(store, options, {
      durable: true,
      previousSchemaVersion: store.previousVersion,
      migrationIssues: store.migrationIssues,
      databaseName,
      now: options.now ?? Date.now,
    });
  } catch (error) {
    const failure = describeStorageFailure(error, { operation: '打开本地数据库' });
    // 这里**不**做任何清理：库保持原样，只是这次不用它
    return await assemble(new MemoryStore(), options, {
      durable: false,
      previousSchemaVersion: 0,
      migrationIssues: [],
      databaseName,
      now: options.now ?? Date.now,
      extraNotice: {
        level: 'warning',
        code: failure.code,
        message: `${failure.message}本次会话的改动不会被保存。`,
        advice: failure.advice,
      },
    });
  }
}

/**
 * 用现成的 store 组装门面。测试用它注入"总是抛配额错误"的存储，
 * 也可以注入 `fake-indexeddb` 驱动的真实实现。
 */
export async function createPersistence(
  store: KeyValueStore,
  options: PersistenceOptions = {},
): Promise<Persistence> {
  return await assemble(store, options, {
    durable: store.kind === 'indexeddb',
    previousSchemaVersion: store.kind === 'indexeddb' ? PERSISTENCE_SCHEMA_VERSION : 0,
    migrationIssues: [],
    databaseName: options.databaseName ?? DEFAULT_DATABASE_NAME,
    now: options.now ?? Date.now,
  });
}

function resolveFactory(options: PersistenceOptions): IDBFactory | null {
  if (options.indexedDB !== undefined) return options.indexedDB;
  const global = (globalThis as { indexedDB?: IDBFactory | undefined }).indexedDB;
  return global ?? null;
}

async function assemble(store: KeyValueStore, options: PersistenceOptions, context: AssembleContext): Promise<Persistence> {
  const notices: PersistenceNotice[] = [];
  const pushNotice = (notice: PersistenceNotice): void => {
    notices.push(notice);
    options.onNotice?.(notice);
  };
  if (context.extraNotice !== undefined) pushNotice(context.extraNotice);

  const reportFailure = (failure: StorageFailure): void => {
    const notice: PersistenceNotice = {
      level: 'error',
      code: failure.code,
      message: failure.message,
      advice: failure.advice,
    };
    // 同一个问题在界面上只该显示一条：轮转失败之类的失败可能连续发生几十次
    if (notices.some((known) => known.message === notice.message)) return;
    pushNotice(notice);
    options.onFailure?.(failure);
  };

  const settings = new SettingsRepository(store, {
    now: context.now,
    onFailure: reportFailure,
    ...(options.settingsDefaults === undefined ? {} : { defaults: options.settingsDefaults }),
  });

  // 审计容量从设置里读：用户在设置页调小过上限，本次启动就按调小后的值执行
  const loaded = await settings.load();
  const audit = new AuditLog(store, {
    now: context.now,
    retention: {
      maxEntries: loaded.settings.audit.maxEntries,
      maxBytes: loaded.settings.audit.maxBytes,
    },
    ...(options.writerId === undefined ? {} : { writerId: options.writerId }),
    onFailure: reportFailure,
    ...(options.auditOnRotate === undefined ? {} : { onRotate: options.auditOnRotate }),
  });

  const params = new ParamSnapshotRepository(store, { now: context.now, onFailure: reportFailure });
  const drafts = new MissionDraftRepository(store, {
    now: context.now,
    onFailure: reportFailure,
    ...(options.maxDrafts === undefined ? {} : { maxDrafts: options.maxDrafts }),
    ...(options.newId === undefined ? {} : { newId: options.newId }),
  });
  const preflight = new PreflightRepository(store, {
    now: context.now,
    onFailure: reportFailure,
    ...(options.maxPreflightRecords === undefined ? {} : { maxRecords: options.maxPreflightRecords }),
    ...(options.newId === undefined ? {} : { newId: options.newId }),
  });

  const status: PersistenceStatus = {
    backend: store.kind === 'indexeddb' ? 'indexeddb' : 'memory',
    durable: context.durable,
    schemaVersion: PERSISTENCE_SCHEMA_VERSION,
    previousSchemaVersion: context.previousSchemaVersion,
    migrationIssues: context.migrationIssues,
    databaseName: context.databaseName,
  };

  return {
    backend: status.backend,
    durable: context.durable,
    store,
    settings,
    params,
    drafts,
    preflight,
    audit,
    status,
    get notices(): readonly PersistenceNotice[] {
      return [...notices];
    },
    dismissNotices(): void {
      notices.length = 0;
    },
    async setAuditRetention(retention: Partial<AuditRetention>): Promise<SaveOutcome<AppSettings>> {
      const current = await settings.load();
      const outcome = await settings.save({
        audit: {
          maxEntries: retention.maxEntries ?? current.settings.audit.maxEntries,
          maxBytes: retention.maxBytes ?? current.settings.audit.maxBytes,
        },
      });
      if (outcome.ok) audit.setRetention(outcome.value.audit);
      return outcome;
    },
    async close(): Promise<void> {
      await store.close();
    },
  };
}
